import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { useEffect, useRef } from 'react';
import { router } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';

interface BannerIntersticialProps {
  onClose: () => void;
}

const BannerIntersticial: React.FC<BannerIntersticialProps> = ({ onClose }) => {
  const { colors } = useTheme();
  const [countdown, setCountdown] = useState(10);
  const isMountedRef = useRef(true);

  // Reativar o contador regressivo para auto-fechamento
  useEffect(() => {
    const timer = setInterval(() => {
      if (!isMountedRef.current) {
        clearInterval(timer);
        return;
      }
      
      setCountdown((prev) => {
        if (prev <= 1) {
          onClose();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [onClose]);
  const handleUpgradeClick = () => {
    onClose();
    if (Platform.OS === 'web') {
      // Para web, navegar para a aba de assinatura
      router.push('/(tabs)/assinatura');
    } else {
      // Para mobile, também navegar para a aba de assinatura
      router.push('/(tabs)/assinatura');
    }
  };

  // Cleanup do componente
  useEffect(() => {
    return () => {
      // Marcar componente como desmontado
      isMountedRef.current = false;
    };
  }, []);

  if (Platform.OS === 'web') {
    return (
      <div style={{
        ...webStyles.overlay,
        backgroundColor: 'rgba(0, 0, 0, 0.9)'
      }}>
        <div style={{
          ...webStyles.container,
          backgroundColor: colors.surface,
          borderColor: colors.border,
          border: `1px solid ${colors.border}`
        }}>
          {/* Contador regressivo */}
          <div style={{
            width: '100%',
            textAlign: 'center' as const,
            marginBottom: '20px'
          }}>
            <p style={{
              fontSize: '14px',
              color: colors.text,
              margin: 0
            }}>
              Fechando em {countdown}s
            </p>
          </div>
          
          {/* Anúncio natural - fallback */}
          <div style={webStyles.fallbackAd}>
            <div style={webStyles.fallbackContent}>
              <h2 style={{
                ...webStyles.fallbackTitle,
                color: colors.primary,
                fontFamily: 'Inter-Bold'
              }}>🚀 Upgrade para Premium</h2>
              <p style={{
                ...webStyles.fallbackText,
                color: colors.text,
                fontFamily: 'Inter-Regular'
              }}>Remova anúncios e tenha acesso a recursos exclusivos!</p>
              <button 
                style={{
                  ...webStyles.upgradeButton,
                  backgroundColor: colors.primary,
                  fontFamily: 'Inter-SemiBold'
                }}
                onClick={handleUpgradeClick}
              >
                Fazer Upgrade
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const dynamicStyles = StyleSheet.create({
    overlay: {
      ...styles.overlay,
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
    },
    container: {
      ...styles.container,
      backgroundColor: colors.surface,
      borderColor: colors.border,
      borderWidth: 1,
    },

    upgradeCard: {
      backgroundColor: colors.card,
      padding: 20,
      borderRadius: 12,
      alignItems: 'center' as const,
      borderWidth: 1,
      borderColor: colors.border,
    },
    upgradeTitle: {
      fontSize: 20,
      fontWeight: 'bold' as const,
      color: colors.text,
      marginBottom: 10,
      textAlign: 'center' as const,
    },
    upgradeDescription: {
      fontSize: 14,
      color: colors.text,
      textAlign: 'center' as const,
      marginBottom: 20,
    },
    upgradeButton: {
      backgroundColor: colors.primary,
      paddingHorizontal: 24,
      paddingVertical: 12,
      borderRadius: 8,
    },
    upgradeButtonText: {
      color: colors.background,
      fontSize: 16,
      fontWeight: 'bold' as const,
    },
    countdownText: {
      fontSize: 14,
      color: colors.text,
      textAlign: 'center' as const,
    },
  });

  return (
    <View style={dynamicStyles.overlay}>
      <View style={dynamicStyles.container}>
        <View style={styles.countdownContainer}>
          <Text style={dynamicStyles.countdownText}>
            Fechando em {countdown}s
          </Text>
        </View>
        
        <View style={styles.fallbackContainer}>
           <View style={dynamicStyles.upgradeCard}>
             <Text style={dynamicStyles.upgradeTitle}>🚀 Upgrade para Premium</Text>
             <Text style={dynamicStyles.upgradeDescription}>
               Remova anúncios e tenha acesso a recursos exclusivos!
             </Text>
             <TouchableOpacity style={dynamicStyles.upgradeButton} onPress={handleUpgradeClick}>
               <Text style={dynamicStyles.upgradeButtonText}>Fazer Upgrade</Text>
             </TouchableOpacity>
           </View>
         </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    zIndex: 9999,
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    width: '90%',
    maxWidth: 400,
    backgroundColor: '#1a1a1a',
    borderRadius: 12,
    padding: 20,
    alignItems: 'center',
  },
  countdownContainer: {
    width: '100%',
    alignItems: 'center',
    marginBottom: 20,
  },
  fallbackContainer: {
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },

});

const webStyles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    zIndex: 9999,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    width: '90%',
    maxWidth: '400px',
    backgroundColor: '#1a1a1a',
    borderRadius: '12px',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
  },
  header: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '20px',
  },
  closeButton: {
    backgroundColor: '#333',
    border: 'none',
    borderRadius: '20px',
    width: '40px',
    height: '40px',
    color: '#fff',
    fontSize: '18px',
    fontWeight: 'bold',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  adContainer: {
    width: '100%',
    minHeight: '300px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  adContent: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: '#fff',
    fontSize: '16px',
  },
  fallbackAd: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  fallbackContent: {
    textAlign: 'center' as const,
    color: '#fff',
    padding: '20px',
  },
  fallbackTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    marginBottom: '15px',
    color: '#4CAF50',
  },
  fallbackText: {
    fontSize: '16px',
    marginBottom: '20px',
    lineHeight: '1.5',
  },
  featureList: {
    listStyle: 'none',
    padding: 0,
    margin: '20px 0',
    textAlign: 'left' as const,
  },
  upgradeButton: {
    backgroundColor: '#4CAF50',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
    marginTop: '15px',
  },
};

export default BannerIntersticial;